/**
 * MODULE: platform/browser/frontend/hooks/useBrowserChat.ts
 * PURPOSE: Chat messages and SSE streaming from browser act endpoint.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type { ChatMessage } from '../types/browser.types';
import {
  formatBrowserActError,
  isBrowserSessionDeadError,
} from '../utils/formatBrowserError';
import { classifyAssistantOutcome } from '../utils/browserAssistantOutcome';
import {
  serverActionsToChatMessages,
  type BrowserSessionActionLogEntry,
} from '../utils/browserChatHydration';

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useBrowserChat(
  sessionId: string | null,
  options?: {
    onObservation?: (message: string) => void;
    onSessionLost?: (lastInstruction?: string) => void;
  },
) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeContext, setActiveContext] = useState<string | undefined>(undefined);
  const onObservationRef = useRef(options?.onObservation);
  const onSessionLostRef = useRef(options?.onSessionLost);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    onObservationRef.current = options?.onObservation;
    onSessionLostRef.current = options?.onSessionLost;
  }, [options?.onObservation, options?.onSessionLost]);

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const sendMessage = useCallback(
    async (userText: string, context?: string) => {
      if (!sessionId || !userText.trim()) return;

      const ctx = (context ?? activeContext)?.trim();
      if (context?.trim()) {
        setActiveContext(context.trim());
      }

      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'user',
          content: userText.trim(),
          timestamp: new Date().toISOString(),
        },
      ]);

      setIsStreaming(true);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const ping = await fetch(
          apiV1Url(`/platform/browser/sessions/${encodeURIComponent(sessionId)}`),
          {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
            signal: controller.signal,
          },
        );
        if (!ping.ok) {
          if (ping.status === 404 || ping.status === 500) {
            onSessionLostRef.current?.(userText.trim());
          }
          throw new Error(formatBrowserActError('Session not found'));
        }

        const body: { instruction: string; context?: string } = {
          instruction: userText.trim(),
        };
        if (ctx) body.context = ctx;

        const res = await fetch(
          apiV1Url(`/platform/browser/sessions/${encodeURIComponent(sessionId)}/act`),
          {
            method: 'POST',
            headers: {
              ...getAuthHeaders(),
              Accept: 'text/event-stream',
            },
            credentials: 'include',
            body: JSON.stringify(body),
            signal: controller.signal,
          },
        );

        if (!res.ok || !res.body) {
          const errBody = await res.json().catch(() => ({}));
          const detail = String(errBody.detail ?? '');
          if (res.status === 404 || isBrowserSessionDeadError(detail)) {
            onSessionLostRef.current?.(userText.trim());
          }
          throw new Error(formatBrowserActError(detail || `Request failed (${res.status})`));
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6)) as { type?: string; message?: string };
              const eventType = event.type ?? 'action';
              const content = event.message ?? '';
              if (eventType === 'observation' && onObservationRef.current) {
                onObservationRef.current(content);
              }
              if (eventType === 'error' && isBrowserSessionDeadError(content)) {
                onSessionLostRef.current?.(userText.trim());
              }
              const displayContent =
                eventType === 'error' ? formatBrowserActError(content) : content;
              setMessages((prev) => [
                ...prev,
                {
                  id: newId(),
                  role:
                    eventType === 'done'
                      ? 'assistant'
                      : eventType === 'error'
                        ? 'error'
                        : 'action',
                  content: displayContent,
                  actionType: eventType as ChatMessage['actionType'],
                  outcome:
                    eventType === 'done' ? classifyAssistantOutcome(content) : undefined,
                  timestamp: new Date().toISOString(),
                },
              ]);
            } catch {
              // skip malformed SSE chunk
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          setMessages((prev) => [
            ...prev,
            {
              id: newId(),
              role: 'action',
              content: 'Stopped — send a new instruction when you are ready.',
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }
        const raw = err instanceof Error ? err.message : 'Failed to run browser command';
        const message = formatBrowserActError(raw);
        if (isBrowserSessionDeadError(raw)) {
          onSessionLostRef.current?.(userText.trim());
        }
        setMessages((prev) => [
          ...prev,
          {
            id: newId(),
            role: 'error',
            content: message,
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        setIsStreaming(false);
      }
    },
    [sessionId, activeContext],
  );

  const clearMessages = useCallback(() => setMessages([]), []);

  const hydrateFromServerActions = useCallback((entries: BrowserSessionActionLogEntry[]) => {
    if (!entries.length) return;
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      return serverActionsToChatMessages(entries);
    });
  }, []);

  return {
    messages,
    sendMessage,
    cancelStream,
    isStreaming,
    clearMessages,
    setActiveContext,
    hydrateFromServerActions,
  };
}
